import { PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import arrayUtil from './array.util';

/**
 * #329 で改名する前の緩和ルール名 → 現在の名前。
 *
 * 緩和ルール名が永続化されるのはブラウザの localStorage(PlanningPresenterSettings) だけなので、
 * 改名した 4 つは旧名のまま残っているブラウザでは `RelaxedRuleValues` に一致せず、
 * 「無効にする制約」のチェックが外れた状態で復元されてしまう。
 *
 * Why not 「指定時間」「指定トラック」も載せる: この 2 つは車両属性のカテゴリ名ではなく
 * 本当に「指定」の意味（指定時間帯・指定されたトラック）なので #329 の接頭辞廃止の対象外で、
 * 改名していない。
 */
const RENAMED_RELAXED_RULES: Record<string, RelaxedRuleVo> = {
  指定車両タイプ: '車両タイプ',
  指定車種: '車種',
  荷台高さ: '荷台高',
  荷台長さ: '荷台長',
};

/**
 * localStorage から読んだ緩和ルール名を現在の名前へ寄せる。
 *
 * Why not 未知のラベルを落とす: 落とすと、まだ `RelaxedRuleValues` に無い項目
 * （別ブランチで足された・将来消された項目）を持つブラウザで選択が黙って消え、
 * この変換で直そうとしている症状そのものを作ってしまう。
 * 緩和フラグを組む buildRelaxParams は `includes` でしか見ないので、
 * 未知の値が混ざっても送信するフラグには影響しない。
 */
export const migrateRelaxedRules = (
  saved: RelaxedRuleVo[] | undefined | null,
): RelaxedRuleVo[] => {
  // localStorage の中身は信用できないので配列かどうかから確かめる
  if (!Array.isArray(saved)) return [];

  return saved.map((rule) => RENAMED_RELAXED_RULES[rule] ?? rule);
};

/**
 * localStorage から読んだ配車条件の緩和ルール名だけを現在の名前へ寄せる。
 *
 * 寄せる必要が無ければ受け取ったオブジェクトをそのまま返す。
 * Why not いつも新しいオブジェクトを返す: この戻り値は自動配車リクエストの
 * planningConditions（履歴スナップショット）にそのまま載るので、
 * 旧名が無いブラウザでキーや参照が変わらないようにしておく。
 */
export const withMigratedRelaxedRules = (conditions: PlanningConditions): PlanningConditions => {
  if (!conditions?.selectedRelaxedRules) return conditions;

  const migrated = migrateRelaxedRules(conditions.selectedRelaxedRules);
  if (arrayUtil.equals(migrated, conditions.selectedRelaxedRules)) return conditions;

  return { ...conditions, selectedRelaxedRules: migrated };
};
